import { useMutation, useQuery } from '@apollo/client';
import { Text } from '../../components/typography';
import CreatableSelect from 'react-select/creatable';
import { loader } from 'graphql.macro';
import styles from './Mitigations.module.scss';
import { useEffect, useMemo, useState } from 'react';
import {
  mitigationLookup,
  mitigationTypes,
} from '../../utilities/risk_registry';
import MitigationCard from './MitigationCard';
import { customColors } from '../../utilities';
import TextWithIcon from '../../components/typography/TextWithIcon';
import Spacer from '../../components/typography/Spacer';

const addMitigationMutation = loader('./Mitigations.add.graphql');
const mitigationQuery = loader('./Mitigations.fetch.graphql');
const updateMitigationMutation = loader('./Mitigations.edit.graphql');

export default function Mitigations({
  isEditing = false,
  mitigations = [],
  setState = () => {},
  state,
  showProps = {
    threats: true,
    consequences: true,
    barriers: true,
    policies: true,
  },
  headerSize = 'md',
  className,
  inline = false,
  required,
}) {
  const [addMitigation] = useMutation(addMitigationMutation);
  const [updateMitigation] = useMutation(updateMitigationMutation);
  const {
    refetch,
    data: {
      threats = [],
      barriers = [],
      consequences = [],
      policies = [],
    } = {},
  } = useQuery(mitigationQuery, { skip: !isEditing });
  const [mitigationsToDelete, setMitigationsToDelete] = useState([]);

  useEffect(() => {
    if (!isEditing) {
      setMitigationsToDelete([]);
    }
  }, [isEditing]);

  const mitigationOptions = useMemo(() => {
    const selectedMitigations = mitigations?.map((m) => m.id) || [];

    const threatOptions = threats
      ?.filter((m) => !selectedMitigations.includes(m.id))
      .map((m) => ({
        label: m.title,
        value: m.id,
        type: mitigationTypes.THREAT.key,
      }));
    const selectedThreats = mitigations?.filter(
      (m) => m.type === mitigationTypes.THREAT.key,
    );

    const barrierOptions = barriers
      ?.filter((m) => !selectedMitigations.includes(m.id))
      .map((m) => ({
        label: m.title,
        value: m.id,
        type: mitigationTypes.BARRIER.key,
      }));
    const selectedBarriers = mitigations?.filter(
      (m) => m.type === mitigationTypes.BARRIER.key,
    );

    const consequenceOptions = consequences
      ?.filter((m) => !selectedMitigations.includes(m.id))
      .map((m) => ({
        label: m.title,
        value: m.id,
        type: mitigationTypes.CONSEQUENCE.key,
      }));
    const selectedConsequences = mitigations?.filter(
      (m) => m.type === mitigationTypes.CONSEQUENCE.key,
    );

    const policyOptions = policies
      ?.filter((m) => !selectedMitigations.includes(m.id))
      .map((m) => ({
        label: m.title,
        value: m.id,
        type: mitigationTypes.POLICY.key,
      }));
    const selectedPolicies = mitigations?.filter(
      (m) => m.type === mitigationTypes.POLICY.key,
    );

    return {
      threatOptions,
      barrierOptions,
      barriers: selectedBarriers,
      threats: selectedThreats,
      consequenceOptions,
      consequences: selectedConsequences,
      policyOptions,
      policies: selectedPolicies,
    };
  }, [threats, barriers, consequences, policies, mitigations]);

  const handleCreate = (title, type) => {
    const group = mitigationLookup[type];
    addMitigation({
      variables: {
        title,
        type,
      },
    }).then(({ data: { addMitigation } }) => {
      const newMitigation = {
        label: addMitigation.title,
        value: addMitigation.id,
        ...addMitigation,
      };
      setState((prevState) => ({
        ...prevState,
        [group]: [...(prevState[group] || []), newMitigation],
      }));
      refetch();
    });
  };
  return (
    <div className={className}>
      <div className={styles.container}>
        {!!showProps.threats && (
          <div className={styles.container}>
            <div className={!!inline ? styles.header : styles.column}>
              <TextWithIcon
                required={required}
                weight="semibold"
                noMargin
                size={headerSize}
              >
                Threats/Hazards
              </TextWithIcon>
              {isEditing && (
                <CreatableSelect
                  isClearable
                  isMulti
                  closeMenuOnSelect={false}
                  onChange={(selected) =>
                    setState((prev) => ({ ...prev, threats: selected }))
                  }
                  onCreateOption={(selected) => {
                    handleCreate(selected, mitigationTypes.THREAT.key);
                  }}
                  options={mitigationOptions.threatOptions || []}
                  placeholder={`Select or add new threat/hazard`}
                  value={state.threats || []}
                />
              )}
            </div>
            <Spacer size="xs" />
            {!!mitigationOptions?.threats?.length ? (
              <div className={styles.flexRow}>
                {mitigationOptions?.threats?.map((threat, i) => {
                  const selected = mitigationsToDelete.includes(threat.id);
                  return (
                    <MitigationCard
                      key={`threat-${threat.id}`}
                      backgroundColor={customColors.ORANGE}
                      isEditing={isEditing}
                      title={threat.title}
                      count={threat.count}
                      onEdit={(title) =>
                        updateMitigation({
                          variables: { id: threat.id, title },
                        })
                      }
                      onDelete={() => {
                        const updatedMitigationsToDelete = selected
                          ? mitigationsToDelete.filter((m) => m !== threat.id)
                          : [...mitigationsToDelete, threat.id];

                        setMitigationsToDelete(updatedMitigationsToDelete);
                        const newState = selected
                          ? state.mitigationsToRemove?.filter(
                              (m) => m !== threat?.id,
                            )
                          : [...(state?.mitigationsToRemove || []), threat?.id];
                        setState((prev) => ({
                          ...prev,
                          mitigationsToRemove: newState,
                        }));
                      }}
                      selected={selected}
                    />
                  );
                })}
              </div>
            ) : (
              !isEditing && <Text noMargin>None</Text>
            )}
          </div>
        )}
        {!!showProps.consequences && (
          <div className={styles.container}>
            <div className={!!inline ? styles.header : styles.column}>
              <TextWithIcon
                required={required}
                weight="semiBold"
                size={headerSize}
                noMargin
              >
                Consequences
              </TextWithIcon>
              {isEditing && (
                <CreatableSelect
                  isClearable
                  isMulti
                  closeMenuOnSelect={false}
                  onChange={(selected) =>
                    setState((prev) => ({ ...prev, consequences: selected }))
                  }
                  onCreateOption={(selected) => {
                    handleCreate(selected, mitigationTypes.CONSEQUENCE.key);
                  }}
                  options={mitigationOptions.consequenceOptions || []}
                  placeholder={`Select or add new consequence`}
                  value={state.consequences || []}
                />
              )}
            </div>
            <Spacer size="xs" />
            {!!mitigationOptions?.consequences?.length ? (
              <div>
                <div className={styles.flexRow}>
                  {mitigationOptions?.consequences?.map((consequence, i) => {
                    const selected = mitigationsToDelete.includes(
                      consequence.id,
                    );
                    return (
                      <MitigationCard
                        key={`consequence-${consequence.id}`}
                        backgroundColor={customColors.RED_LIGHT}
                        isEditing={isEditing}
                        title={consequence.title}
                        count={consequence.count}
                        onEdit={(title) =>
                          updateMitigation({
                            variables: { id: consequence.id, title },
                          })
                        }
                        onDelete={() => {
                          const updatedMitigationsToDelete = selected
                            ? mitigationsToDelete.filter(
                                (m) => m !== consequence.id,
                              )
                            : [...mitigationsToDelete, consequence.id];

                          setMitigationsToDelete(updatedMitigationsToDelete);
                          const newState = selected
                            ? state.mitigationsToRemove?.filter(
                                (m) => m !== consequence?.id,
                              )
                            : [
                                ...(state?.mitigationsToRemove || []),
                                consequence?.id,
                              ];
                          setState((prev) => ({
                            ...prev,
                            mitigationsToRemove: newState,
                          }));
                        }}
                        selected={selected}
                      />
                    );
                  })}
                </div>
              </div>
            ) : (
              !isEditing && <Text noMargin>None</Text>
            )}
          </div>
        )}
        {!!showProps.barriers && (
          <div className={styles.container}>
            <div className={!!inline ? styles.header : styles.column}>
              <TextWithIcon
                required={required}
                weight="semibold"
                size={headerSize}
                noMargin
              >
                Barriers/Controls
              </TextWithIcon>
              <div>
                {isEditing && (
                  <CreatableSelect
                    isClearable
                    isMulti
                    closeMenuOnSelect={false}
                    onChange={(selected) =>
                      setState((prev) => ({ ...prev, barriers: selected }))
                    }
                    onCreateOption={(selected) => {
                      handleCreate(selected, mitigationTypes.BARRIER.key);
                    }}
                    options={mitigationOptions?.barrierOptions || []}
                    placeholder={`Select or add new barrier or control`}
                    value={state.barriers || []}
                  />
                )}
              </div>
            </div>
            <Spacer size="xs" />
            <div className={styles.flexRow}>
              {!!mitigationOptions?.barriers?.length
                ? mitigationOptions?.barriers?.map((barrier, i) => {
                    const selected = mitigationsToDelete.includes(barrier.id);
                    return (
                      <MitigationCard
                        key={`barrier-${barrier.id}`}
                        isEditing={isEditing}
                        count={barrier.count}
                        title={barrier.title}
                        onEdit={(title) =>
                          updateMitigation({
                            variables: { id: barrier.id, title },
                          })
                        }
                        onDelete={() => {
                          const updatedMitigationsToDelete = selected
                            ? mitigationsToDelete.filter(
                                (m) => m !== barrier.id,
                              )
                            : [...mitigationsToDelete, barrier.id];

                          setMitigationsToDelete(updatedMitigationsToDelete);

                          const newState = selected
                            ? state.mitigationsToRemove?.filter(
                                (m) => m !== barrier?.id,
                              )
                            : [
                                ...(state?.mitigationsToRemove || []),
                                barrier?.id,
                              ];
                          setState((prev) => ({
                            ...prev,
                            mitigationsToRemove: newState,
                          }));
                        }}
                        selected={selected}
                      />
                    );
                  })
                : !isEditing && <Text noMargin>None</Text>}
            </div>
          </div>
        )}
        {!!showProps.policies && (
          <div className={styles.container}>
            <div className={!!inline ? styles.header : styles.column}>
              <TextWithIcon
                required={required}
                weight="semibold"
                size={headerSize}
                noMargin
              >
                Policies/Procedures
              </TextWithIcon>
              <div>
                {isEditing && (
                  <CreatableSelect
                    isClearable
                    isMulti
                    closeMenuOnSelect={false}
                    onChange={(selected) =>
                      setState((prev) => ({ ...prev, policies: selected }))
                    }
                    onCreateOption={(selected) => {
                      handleCreate(selected, mitigationTypes.POLICY.key);
                    }}
                    options={mitigationOptions?.policyOptions || []}
                    placeholder={`Select or add new policy`}
                    value={state.policies || []}
                  />
                )}
              </div>
            </div>
            <Spacer size="xs" />
            <div className={styles.flexRow}>
              {!!mitigationOptions?.policies?.length
                ? mitigationOptions?.policies?.map((policy, i) => {
                    const selected = mitigationsToDelete.includes(policy.id);
                    return (
                      <MitigationCard
                        key={`policy-${policy.id}`}
                        backgroundColor={customColors.GREEN_ULTRA_LIGHT}
                        isEditing={isEditing}
                        count={policy.count}
                        title={policy.title}
                        onEdit={(title) =>
                          updateMitigation({
                            variables: { id: policy.id, title },
                          })
                        }
                        onDelete={() => {
                          const updatedMitigationsToDelete = selected
                            ? mitigationsToDelete.filter((m) => m !== policy.id)
                            : [...mitigationsToDelete, policy.id];

                          setMitigationsToDelete(updatedMitigationsToDelete);

                          const newState = selected
                            ? state.mitigationsToRemove?.filter(
                                (m) => m !== policy?.id,
                              )
                            : [
                                ...(state?.mitigationsToRemove || []),
                                policy?.id,
                              ];
                          setState((prev) => ({
                            ...prev,
                            mitigationsToRemove: newState,
                          }));
                        }}
                        selected={selected}
                      />
                    );
                  })
                : !isEditing && <Text noMargin>None</Text>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
